/**
 * Theme mode plumbing: light / dark / follow-the-system.
 *
 * Dark mode is wired in from the start rather than retrofitted, because
 * retrofitting means auditing every component for hardcoded light-mode
 * assumptions — exactly the work the token file exists to prevent.
 *
 * The chosen mode is a UI preference, not a secret, so it lives in
 * localStorage. The Canvas token is the only thing that goes in IndexedDB
 * (brainstorm.md 1.15).
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { buildTheme } from './theme';
import {
  ThemeModeContext,
  type ThemeModeContextValue,
  type ThemePreference,
} from './themeMode';
import { type ThemeMode } from './tokens';

const STORAGE_KEY = 'cgc.theme-preference';

function readStoredPreference(): ThemePreference {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  } catch {
    // Storage can be unavailable (private mode, or a managed-device policy).
    // Falling back to `system` is strictly better than crashing the app.
  }
  return 'system';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readStoredPreference);
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)', { noSsr: true });

  const mode: ThemeMode = preference === 'system' ? (prefersDark ? 'dark' : 'light') : preference;

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Preference simply will not persist; the app still works.
    }
  }, []);

  const toggle = useCallback(() => {
    setPreferenceState((current) => {
      const resolved = current === 'system' ? (prefersDark ? 'dark' : 'light') : current;
      const next: ThemePreference = resolved === 'dark' ? 'light' : 'dark';
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* non-fatal */
      }
      return next;
    });
  }, [prefersDark]);

  const theme = useMemo(() => buildTheme(mode), [mode]);

  // Keep the browser UI (address bar, form controls, scrollbars) in step.
  useEffect(() => {
    document.documentElement.style.colorScheme = mode;
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', theme.palette.background.default);
  }, [mode, theme]);

  const value = useMemo<ThemeModeContextValue>(
    () => ({ preference, mode, setPreference, toggle }),
    [preference, mode, setPreference, toggle],
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeModeContext.Provider>
  );
}
